import { useRef, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, isBefore, addDays } from "date-fns";
import { CalendarIcon, Info, Upload, X, BabyIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  apiCreateLeaveForEmployee,
  apiGetEmployeeLeaveBalance,
  apiGetEmployees,
  apiGetLeaveDocumentRequirement,
  apiGetLeaveTypes,
  apiGetExtendedLeavePolicy,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getLeaveApiErrorMessage } from "@/lib/leave-i18n";

interface ManualLeaveRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualLeaveRequestDialog({
  open,
  onOpenChange,
}: ManualLeaveRequestDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dateLocale = useDateLocale();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [employeeId, setEmployeeId] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const startDateIso = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const endDateIso = endDate ? format(endDate, "yyyy-MM-dd") : "";
  const selectedEmployeeId = employeeId ? Number(employeeId) : undefined;

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    enabled: open,
  });


  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: apiGetLeaveTypes,
    enabled: open,
  });

  const { data: balances = [] } = useQuery({
    queryKey: ["employeeLeaveBalance", selectedEmployeeId, startDate?.getFullYear()],
    queryFn: () => apiGetEmployeeLeaveBalance(selectedEmployeeId!, startDate?.getFullYear()),
    enabled: open && !!selectedEmployeeId,
  });

  // Extended leave: override toggle
  const [extendedOverride, setExtendedOverride] = useState(false);

  // Detect if selected leave type is extended
  const selectedLeaveTypeMeta = leaveTypes.find(lt => lt.name === leaveType);
  const isExtended = Boolean((selectedLeaveTypeMeta as any)?.isExtendedLeave);

  // Fetch extended policy when type + employee are selected
  const { data: extendedPolicy } = useQuery({
    queryKey: ["extendedLeavePolicy", leaveType, selectedEmployeeId],
    queryFn: () => apiGetExtendedLeavePolicy(leaveType, { employeeId: selectedEmployeeId }),
    enabled: isExtended && !!leaveType && !!selectedEmployeeId,
    staleTime: 30_000,
  });

  // Auto-calculate end date when policy is resolved and start date is set
  useEffect(() => {
    if (!isExtended || extendedOverride) return;
    if (!startDate || !extendedPolicy?.durationDays) return;
    const calculatedEnd = addDays(startDate, extendedPolicy.durationDays - 1);
    setEndDate(calculatedEnd);
  }, [isExtended, extendedOverride, startDate, extendedPolicy?.durationDays]);

  // Reset override when leave type changes
  useEffect(() => {
    setExtendedOverride(false);
    if (!isExtended) {
      // Don't clear end date for regular types
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveType]);

  const { data: documentRequirement } = useQuery({
    queryKey: [
      "leaveDocumentRequirement",
      "manual",
      selectedEmployeeId,
      leaveType,
      startDateIso,
      endDateIso,
    ],
    queryFn: () =>
      apiGetLeaveDocumentRequirement({
        employeeId: selectedEmployeeId,
        leaveType,
        startDate: startDateIso,
        endDate: endDateIso,
      }),
    enabled: open && !!selectedEmployeeId && !!leaveType && !!startDateIso && !!endDateIso,
  });

  const balanceForType = balances.find((balance) => balance.leaveType === leaveType);
  const isBalanceBasedSelectedType =
    balanceForType?.countsAgainstBalance && leaveType !== "special";
  const workingDays = documentRequirement?.workingDays ?? 0;
  const availableAfter =
    balanceForType && isBalanceBasedSelectedType
      ? (balanceForType.availableNow ?? 0) - workingDays
      : null;
  const balanceExceeded = availableAfter !== null && availableAfter < 0 && leaveType !== "unpaid" && leaveType !== "sick";

  const createMutation = useMutation({
    mutationFn: (formData: FormData) => apiCreateLeaveForEmployee(formData),
    onSuccess: () => {
      toast.success(t("leave.manualCreateSuccess"));
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["notificationCount"] });
      handleClose();
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err, "leave.manualCreateFailed")),
  });

  function handleClose() {
    setEmployeeId("");
    setLeaveType("");
    setStartDate(undefined);
    setEndDate(undefined);
    setNotes("");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onOpenChange(false);
  }

  function submit() {
    if (!selectedEmployeeId || !leaveType || !startDate || !endDate) {
      toast.error(t("leave.manualCreateMissingFields"));
      return;
    }
    if (isBefore(endDate, startDate)) {
      toast.error(t("leave.endDateAfterStart"));
      return;
    }
    if (balanceExceeded) {
      toast.error(t("leave.insufficientBalance", {
        available: balanceForType?.availableNow ?? 0,
        requested: workingDays,
      }));
      return;
    }

    const formData = new FormData();
    formData.append("employeeId", String(selectedEmployeeId));
    formData.append("leaveType", leaveType);
    formData.append("startDate", format(startDate, "yyyy-MM-dd"));
    formData.append("endDate", format(endDate, "yyyy-MM-dd"));
    if (notes.trim()) formData.append("notes", notes.trim());
    if (selectedFile) formData.append("document", selectedFile);
    createMutation.mutate(formData);
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : handleClose())}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("leave.createLeaveForEmployee")}</DialogTitle>
          <DialogDescription>{t("leave.createLeaveForEmployeeDesc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("leave.employee")}</Label>
            <EmployeeCombobox
              employees={employees.filter(employee => String(employee.id) !== String(user?.employeeId))}
              value={employeeId}
              onChange={(id) => {
                setEmployeeId(id);
                setStartDate(undefined);
                setEndDate(undefined);
              }}
              placeholder={t("leave.selectEmployee")}
            />
          </div>

          {/* Extended leave info banner */}
          {isExtended && (
            <Alert className="border-pink-500/30 bg-pink-500/5">
              <BabyIcon className="h-4 w-4 text-pink-400" />
              <AlertDescription className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">
                    {t("leave.extended.hrOnlyNotice", "Extended leave — HR managed")}
                  </span>
                  {extendedPolicy?.source && (
                    <Badge variant="outline" className="text-xs shrink-0">
                      {extendedPolicy.source === "country_default"
                        ? t("leave.extended.sourceCountryDefault", "Country Default")
                        : extendedPolicy.source === "tenant_override"
                          ? t("leave.extended.sourceTenantOverride", "Custom Override")
                          : t("leave.extended.sourceNotConfigured", "Not Configured")}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("leave.extended.accessModeNotice",
                    "Approving this leave will set the employee to 'On Leave' status with Limited Access mode. New leave requests and attendance actions will be paused until reactivation.")}
                </p>
                {extendedPolicy?.durationDays && (
                  <p className="text-xs font-medium">
                    {t("leave.extended.effectiveDuration", "Effective duration:")}{" "}
                    <span className="text-foreground">{extendedPolicy.durationDays} {t("leave.calendarDays", "calendar days")}</span>
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label>{t("leave.leaveType")}</Label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger>
                <SelectValue placeholder={t("leave.selectLeaveType")} />
              </SelectTrigger>
              <SelectContent>
                {leaveTypes.map((type) => (
                  <SelectItem key={type.name} value={type.name}>
                    {t(`leave.types.${type.name}`, { defaultValue: type.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date pickers — extended leave shows auto-calculated end date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("leave.startDate")}</Label>
              <Popover open={startOpen} onOpenChange={setStartOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "d MMM yyyy", { locale: dateLocale }) : t("leave.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartOpen(false);
                    }}
                    locale={dateLocale}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t("leave.endDate")}</Label>
                {isExtended && extendedPolicy?.allowManualOverride && (
                  <div className="flex items-center gap-1.5">
                    <Switch
                      id="extended-override-switch"
                      checked={extendedOverride}
                      onCheckedChange={setExtendedOverride}
                      className="scale-75"
                    />
                    <Label htmlFor="extended-override-switch" className="text-xs text-muted-foreground cursor-pointer">
                      {t("leave.extended.override", "Override")}
                    </Label>
                  </div>
                )}
              </div>
              {isExtended && !extendedOverride ? (
                // Auto-calculated — read-only display
                <div className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
                  endDate ? "bg-muted/30" : "text-muted-foreground",
                )}>
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  {endDate
                    ? <span className="font-medium">{format(endDate, "d MMM yyyy", { locale: dateLocale })}</span>
                    : <span className="text-muted-foreground">{t("leave.extended.autoCalculated", "Auto-calculated")}</span>}
                </div>
              ) : (
                // Standard picker (regular types or override)
                <Popover open={endOpen} onOpenChange={setEndOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start", !endDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "d MMM yyyy", { locale: dateLocale }) : t("leave.pickDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        setEndDate(date);
                        setEndOpen(false);
                      }}
                      disabled={(date) => !!startDate && isBefore(date, startDate)}
                      locale={dateLocale}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {documentRequirement && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("leave.workingDaysRequested")}</span>
                <span className="font-semibold">{documentRequirement.workingDays}</span>
              </div>
              {isBalanceBasedSelectedType && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t("leave.afterRequest")}</span>
                  <span className={cn("font-semibold", balanceExceeded ? "text-destructive" : "text-emerald-600")}>
                    {availableAfter}
                  </span>
                </div>
              )}
            </div>
          )}

          {balanceExceeded && (
            <Alert variant="destructive">
              <AlertDescription>
                {t("leave.insufficientBalance", {
                  available: balanceForType?.availableNow ?? 0,
                  requested: workingDays,
                })}
              </AlertDescription>
            </Alert>
          )}

          {documentRequirement?.documentType && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">{documentRequirement.documentLabel}</span>
                {documentRequirement.documentDescription ? ` - ${documentRequirement.documentDescription}` : ""}
                {!selectedFile && documentRequirement.mandatoryUpload && (
                  <span className="block mt-1 text-muted-foreground">
                    {t("leave.employeeWillNeedDocument")}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label>{t("leave.supportingDocumentOptional")}</Label>
            {selectedFile ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate">{selectedFile.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50">
                <Upload className="h-4 w-4" />
                <span>{t("leave.uploadHint")}</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
                />
              </label>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t("leave.notes")}</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t("leave.notesPlaceholder")}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button type="button" onClick={submit} disabled={createMutation.isPending || balanceExceeded}>
              {createMutation.isPending ? t("leave.submitting") : t("leave.createLeave")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
